import type { GameKind } from "@minigames/shared";

export interface GameRules {
  /** Одна фраза: что вообще требуется сделать. */
  goal: string;
  /** Как ходить — по шагам, коротко. */
  how: string[];
  /** Что важно знать, чтобы не растеряться. */
  notes: string[];
  /** Подсказка при первом заходе — одно предложение поверх доски. */
  hint: string;
}

/**
 * Правила игр для тех, кто видит их впервые.
 *
 * Пишем не «по учебнику», а по делу: что нажимать и чего добиваться.
 * Проверено на живом примере — человек открыл реверси и не понял, что делать.
 */
export const RULES: Record<GameKind, GameRules> = {
  chess: {
    goal: "Поставить мат королю соперника.",
    how: [
      "Тапни свою фигуру — подсветятся поля, куда она может пойти.",
      "Тапни поле или перетащи фигуру пальцем.",
      "Пешка, дошедшая до конца доски, становится ферзём.",
    ],
    notes: [
      "Часы идут только у того, чей ход. Кончилось время — поражение.",
      "Можно предложить ничью или сдаться кнопками под доской.",
    ],
    hint: "Тапни фигуру — увидишь, куда ей можно пойти.",
  },

  durak: {
    goal: "Избавиться от карт. Проигрывает тот, кто остался с картами последним.",
    how: [
      "Когда твой ход — тапни карту, чтобы зайти.",
      "Защищаясь, тапни карту, которой бьёшь. Если вариантов несколько — потом тапни карту на столе.",
      "Не можешь отбиться — жми «Беру». Отбился и добавить нечего — «Бито».",
    ],
    notes: [
      "Козырь показан слева под колодой: любая козырная бьёт любую некозырную.",
      "Подкидывать можно только карты того ранга, что уже лежат на столе.",
      "Тусклые карты сейчас сыграть нельзя.",
    ],
    hint: "Тапни карту, чтобы сходить. Тусклыми ходить нельзя.",
  },

  checkers: {
    goal: "Съесть все шашки соперника или лишить его ходов.",
    how: [
      "Тапни свою шашку, потом поле, куда идёшь.",
      "Бьёшь несколько подряд — тапай поля приземления по очереди.",
      "Дошёл до последнего ряда — шашка становится дамкой и ходит на любое расстояние.",
    ],
    notes: [
      "Бить обязательно: если есть чем, другие ходы недоступны.",
      "Простая шашка бьёт и вперёд, и назад.",
      "Подсвечены только те поля, куда реально можно пойти.",
    ],
    hint: "Тапни шашку, затем поле. Если можешь бить — придётся бить.",
  },

  reversi: {
    goal: "К концу партии иметь больше своих фишек на доске.",
    how: [
      "Ставь фишку так, чтобы между ней и любой твоей фишкой оказался сплошной ряд чужих.",
      "Все зажатые чужие фишки переворачиваются и становятся твоими.",
      "Ход обязан хоть что-то переворачивать — иначе он недоступен.",
    ],
    notes: [
      "Точками подсвечены все клетки, куда можно пойти прямо сейчас.",
      "Углы — самое ценное: захваченную угловую фишку перевернуть уже нельзя.",
      "Ходить некуда — ход автоматически переходит сопернику.",
    ],
    hint: "Ставь фишку так, чтобы зажать чужие между своими — они перевернутся.",
  },

  gomoku: {
    goal: "Собрать пять своих камней подряд — по горизонтали, вертикали или диагонали.",
    how: ["Тапни любое свободное пересечение, чтобы поставить камень."],
    notes: [
      "Побеждает пять и больше подряд, длинный ряд не запрещён.",
      "Следи за соперником: открытую четвёрку надо перекрывать сразу.",
    ],
    hint: "Ставь камни в ряд по пять. Не забывай перекрывать соперника.",
  },

  seabattle: {
    goal: "Потопить весь флот соперника раньше, чем он потопит твой.",
    how: [
      "Сначала расставь корабли: жми «Другая расстановка», пока вариант не понравится, потом «Я готов».",
      "В бою тапни клетку на поле соперника — это выстрел.",
      "Попал — стреляешь снова. Промахнулся — ход переходит.",
    ],
    notes: [
      "Флот: один четырёхпалубный, два трёхпалубных, три двухпалубных, четыре однопалубных.",
      "Корабли не касаются друг друга даже углами.",
      "Вокруг потопленного корабля клетки открываются сами — туда стрелять уже незачем.",
      "Расставляете вы оба одновременно, ждать соперника не нужно.",
    ],
    hint: "Расставь флот и жми «Я готов». Потом тапай по полю соперника.",
  },
};
